'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useStore } from 'zustand';

import { PatientSelectionModal } from './patient-selection-modal.component';
import { buildPatientOptions } from './_utils/build-patient-options.util';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { SearchType } from '@/app/(main)/_components/search-modal/search-modal.types';
import { authStore } from '@/store/auth.store';
import {
  closePatientSelection,
  patientSelectionStore,
  selectPatient,
  setPendingPatient,
} from '@/store/patient-selection/patient-selection.store';
import { handleOpenSearchModal } from '@/store/search/handle-open-search-modal';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { SearchModalOriginEnum } from '@/types/enums/analytics/home/home-analytics.enum';
import { EntityProfileType } from '@/types/enums/entity-profile-type.enum';
import { SearchResultsFilter } from '@/types/search-results-filter.type';
import { getCartOrigin } from '@/utils/cart/get-cart-origin.util';
import { getEntityProfileType } from '@/utils/entity/get-entity-profile-type.util';

const SEARCH_FILTER_BY_PROFILE_TYPE: Record<EntityProfileType, SearchResultsFilter> = {
  [EntityProfileType.PROFESSIONAL]: SearchType.PROFESSIONAL,
  [EntityProfileType.PARTNER]: SearchType.PARTNER,
  [EntityProfileType.LABORATORY]: SearchType.PARTNER,
};

export function PatientSelectionHost() {
  const isOpen = useStore(patientSelectionStore, (state) => state.isOpen);
  const request = useStore(patientSelectionStore, (state) => state.request);
  const pendingPatient = useStore(patientSelectionStore, (state) => state.pendingPatient);
  const authenticatedUser = useStore(authStore, (state) => state.authenticatedUser);
  const phoneConfirmedAt = useStore(authStore, (state) => state.phoneConfirmedAt);

  const pathname = usePathname();
  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    if (prevPathnameRef.current === pathname) {
      return;
    }

    prevPathnameRef.current = pathname;
    closePatientSelection();
  }, [pathname]);

  const ageRange = useMemo(
    () => ({ minAge: request?.ageRange.minAge, maxAge: request?.ageRange.maxAge }),
    [request?.ageRange.minAge, request?.ageRange.maxAge],
  );

  const patients = useMemo(
    () => buildPatientOptions(authenticatedUser, ageRange),
    [authenticatedUser, ageRange],
  );

  const [holderPhone = ''] = authenticatedUser?.contact?.phones ?? [];

  const canSearchAnotherOption = request?.canSearchAnotherOption ?? true;

  const shouldConfirmPhone = !phoneConfirmedAt;

  const searchAnotherOption = useCallback(() => {
    const entityProfileType = getEntityProfileType(pathname);

    getAnalyticsPlugin().track(AnalyticsEnum.CLICK_SEARCH_ANOTHER_OPTION, {
      origem: getCartOrigin(pathname),
      tipo_prestador: entityProfileType,
    });

    closePatientSelection();

    if (!entityProfileType) {
      return;
    }

    handleOpenSearchModal({
      origin: SearchModalOriginEnum.PATIENT_SELECTION,
      preselectedFilter: SEARCH_FILTER_BY_PROFILE_TYPE[entityProfileType],
      skipLocationModal: true,
    });
  }, [pathname]);

  return (
    <PatientSelectionModal
      open={isOpen}
      patients={patients}
      ageRange={ageRange}
      requiresPhoneConfirmation={shouldConfirmPhone}
      canSearchAnotherOption={canSearchAnotherOption}
      pendingPatient={pendingPatient}
      holderPhone={holderPhone}
      onClose={closePatientSelection}
      onSelectPatient={selectPatient}
      onRequestPhoneConfirmation={setPendingPatient}
      onSearchAnotherOption={searchAnotherOption}
    />
  );
}
